<template>
    <form>
        <q-stepper
            v-model="step"
            ref="stepper"
            color="primary"
            animated
            flat
        >
            <q-step
                :name="1"
                title="User profile"
                icon="fa fa-user"
                active-color="primary"
                :error="step1_error"
                :done="step > 1"
            >
                <div class="row q-col-gutter-md">
                    <div class="col col-xs-12 col-md-6 col-lg-3">
                        <q-input
                            v-model="UserObj.first_name"
                            label="First name"
                            lazy-rules
                            :error="UserObj.errors.has('first_name')"
                            :error-message="UserObj.errors.get('first_name')"
                            :rules="[ val => val && val.length > 0 || 'First name is required!']"
                            color="primary"
                        >
                            <template v-slot:prepend>
                                <q-btn-dropdown
                                    v-model="displayGenders"
                                    :icon="userGender.icon"
                                    :color="userGender.color"
                                    :loading="loading"
                                    flat
                                    dense
                                    clearable
                                >
                                    <q-list>
                                        <q-item clickable
                                                v-close-popup
                                                v-for="gender in genders"
                                                :key="gender.value"
                                                @click="setUserGender(gender)"
                                        >
                                            <q-item-section>
                                                <q-item-label>
                                                    <q-icon :name="gender.icon" :color="gender.color" size="18px"/>
                                                    {{ gender.label }}
                                                </q-item-label>
                                            </q-item-section>
                                        </q-item>
                                    </q-list>
                                </q-btn-dropdown>
                            </template>
                        </q-input>
                    </div> <!-- First name -->
                    <div class="col col-xs-12 col-md-6 col-lg-3">
                        <q-input
                            v-model="UserObj.middle_name"
                            label="Middle name"
                            lazy-rules
                            :error="UserObj.errors.has('middle_name')"
                            :error-message="UserObj.errors.get('middle_name')"
                            color="primary"
                        />
                    </div> <!-- Middle name -->
                    <div class="col col-xs-12 col-md-6 col-lg-3">
                        <q-input
                            v-model="UserObj.last_name"
                            label="Last name"
                            :error="UserObj.errors.has('last_name')"
                            :error-message="UserObj.errors.get('last_name')"
                            lazy-rules
                            :rules="[ val => val && val.length > 0 || 'Last name is required!']"
                            color="primary"
                        />
                    </div> <!-- Last Name -->
                    <div class="col col-xs-12 col-md-6 col-lg-3">
                        <q-input
                            v-model="UserObj.date_of_birth"
                            label="Date of birth"
                            mask="date"
                            :error="UserObj.errors.has('date_of_birth')"
                            :error-message="UserObj.errors.get('date_of_birth')"
                            color="primary"
                        >
                            <template v-slot:prepend>
                                <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                                    <q-popup-proxy ref="qDateProxy" transition-show="scale" transition-hide="scale">
                                        <q-date
                                            v-model="UserObj.date_of_birth"
                                            @input="() => $refs.qDateProxy.hide()"
                                        />
                                    </q-popup-proxy>
                                </q-icon>
                            </template>
                        </q-input>
                    </div> <!-- Date of birth -->

                    <div class="col col-xs-12 col-md-3 col-lg-3">
                        <q-select
                            v-model="UserObj.country_id"
                            :label="$t('banks.table_titles.country')"
                            emit-value
                            map-options
                            use-input
                            option-value="id"
                            option-label="name"
                            :options="filtered_countries"
                            :loading="loading"
                            :error="UserObj.errors.has('country_id')"
                            :error-message="UserObj.errors.get('country_id')"
                            :rules="[ val => val && val > 0 || $t('banks.table_titles.country') + ' is required!']"
                            color="input-color"
                            @filter="filterCountries"
                        >
                            <template v-slot:option="scope">
                                <q-item
                                    v-bind="scope.itemProps"
                                    v-on="scope.itemEvents"
                                    style="border-bottom:1px solid #999"
                                >
                                    <q-item-section avatar>
                                        <q-icon :name="scope.opt.acronym" size="xs" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label>{{scope.opt.name}}</q-item-label>
                                    </q-item-section>
                                </q-item>
                            </template>
                            <template v-slot:prepend>
                                <q-icon :name="userCountry.acronym"/>
                            </template>
                        </q-select>
                    </div> <!-- Country -->

                    <div class="col col-xs-12 col-md-6 col-lg-3">
                        <q-input
                            v-model="UserObj.mobile"
                            mask="####################"
                            color="primary"
                            clearable
                            lazy-rules
                            :error="UserObj.errors.has('mobile')"
                            :error-message="UserObj.errors.get('mobile')"
                            ref="mobile"
                        >
                            <template v-slot:prepend>
                                <q-btn-dropdown
                                    v-model="displayCallingCodes"
                                    :label="'+' + UserObj.calling_code"
                                    :icon="userCountryByCallingCode.acronym"
                                    :loading="loading"
                                    flat
                                    dense
                                >
                                    <q-list>
                                        <q-item clickable
                                                v-close-popup
                                                v-for="country in countries"
                                                :key="country.id"
                                                @click="setCurrentCountry(country)"
                                        >
                                            <q-item-section>
                                                <q-item-label>
                                                    <q-icon :name="country.acronym" size="18px"/>
                                                    {{ country.name }}
                                                </q-item-label>
                                            </q-item-section>
                                            <q-item-section side>
                                                <q-item-label>+{{ country.calling_code }}</q-item-label>
                                            </q-item-section>
                                        </q-item>
                                    </q-list>
                                </q-btn-dropdown>
                            </template>
                        </q-input>
                    </div> <!-- Mobile -->
                    <div class="col col-xs-12 col-md-6 col-lg-6">
                        <q-input
                            v-model="UserObj.email"
                            label="Email"
                            :error="UserObj.errors.has('email')"
                            :error-message="UserObj.errors.get('email')"
                            lazy-rules
                            :rules="[ val => val && $options.filters.validateEmail(val) || 'Invalid email address']"
                            color="primary"
                        />
                    </div> <!-- Email -->

                    <div class="col col-xs-12 col-md-6 col-lg-3">
                        <q-input
                            v-model="UserObj.password"
                            type="password"
                            label="Password"
                            lazy-rules
                            :error="UserObj.errors.has('password')"
                            :error-message="UserObj.errors.get('password')"
                            :rules="[ val => $options.filters.validatePassword(val) || 'Password should be at least 8 characters, and include 1 letter and 1 number.']"
                            input-class="text-center"
                            color="primary"
                        />
                    </div> <!-- Password -->
                    <div class="col col-xs-12 col-md-6 col-lg-3">
                        <q-input
                            v-model="UserObj.password_confirmation"
                            type="password"
                            label="Password confirmation"
                            lazy-rules
                            :error="UserObj.errors.has('password_confirmation')"
                            :error-message="UserObj.errors.get('password_confirmation')"
                            :rules="[ val => val === UserObj.password || 'Password confirmation does not match.']"
                            input-class="text-center"
                            color="primary"
                        />
                    </div> <!-- Password Confirmation -->
                    <div class="col col-xs-12 col-md-6 col-lg-6">
                        <q-select
                            label="Address"
                            :value="UserObj.address"
                            use-input
                            hide-selected
                            fill-input
                            input-debounce="0"
                            :options="addresses"
                            @filter="filterAddresses"
                            @input-value="setAddress"
                            hide-dropdown-icon
                            placeholder="Start typing the address..."
                            :error="UserObj.errors.has('address')"
                            :error-message="UserObj.errors.get('address')"
                            color="primary"
                        />
                    </div> <!-- Address -->

                    <div class="col col-xs-12 col-sm-6 col-md-3">
                        <q-select
                            v-model="UserObj.account_type"
                            :options="account_types"
                            label="Account type"
                            color="primary"
                        />
                    </div> <!-- Account type -->

                    <div class="col col-xs-12 col-sm-6 col-md-3">
                        <p class="text-caption q-pb-xs" style="margin:0">ECDD</p>
                        <q-toggle
                            v-model="UserObj.ecdd"
                            checked-icon="fa fa-check"
                            color="green"
                            :label="UserObj.ecdd ? 'ECDD completed' : 'ECDD not required'"
                            unchecked-icon="fa fa-uncheck"
                        />
                    </div> <!-- ECDD -->
                    <div class="col col-xs-12 col-sm-6 col-md-3">
                        <p class="text-caption q-pb-xs" style="margin:0">Activation state</p>
                        <q-toggle
                            v-model="UserObj.is_blocked"
                            checked-icon="fa fa-check"
                            color="red"
                            :label="UserObj.is_blocked ? 'Blocked' : 'Normal'"
                            unchecked-icon="fa fa-uncheck"
                        />
                    </div> <!-- IS_BLOCKED -->
                </div>

                <q-separator/>

                <q-stepper-navigation class="flex justify-end">
                    <q-btn color="primary" @click="$refs.stepper.next()" label="Access Level Control"
                           icon-right="fa fa-arrow-right" v-if="$auth.can('acl.edit')"/>
                    <q-btn color="cancel-btn" icon="fa fa-save" :label="$t('save')" @click="submit" :loading="loading"
                           v-else/>
                </q-stepper-navigation>
            </q-step>

            <q-step
                :name="2"
                title="Access Level Control"
                icon="fa fa-shield-alt"
                active-color="primary"
                :done="step === 2"
                v-if="$auth.can('acl.edit')"
            >
                <div class="row q-col-gutter-md q-pb-lg">
                    <div class="col col-xs-12 col-md-6 col-lg-3">
                        <q-select
                            v-model="UserObj.role"
                            :options="roles"
                            option-value="name"
                            option-label="label"
                            label="User's role"
                            clearable
                            @input="provide_permissions"
                            emit-value
                            map-options
                        />
                    </div>
                </div>
                <div class="row q-col-gutter-md">
                    <div class="col col-xs-12 text-center" v-if="permissions_loading">
                        <q-spinner-dots color="primary" size="30px"/>
                    </div>
                    <div class="col col-xs-12 col-md-6 col-lg-3" v-for="(permission, index) in permissions" :key="index"
                         v-else>
                        <q-checkbox
                            :val="permission.name"
                            :label="permission.label"
                            :disable="permission.checked"
                            v-model="UserObj.permissions"
                            color="primary"/>
                    </div>
                </div>

                <q-separator/>

                <q-stepper-navigation class="flex justify-between">
                    <q-btn color="cancel-btn" flat @click="$refs.stepper.previous()" label="Back"/>
                    <q-btn color="primary" icon="fa fa-save" label="Save" @click="submit" :loading="loading"/>
                </q-stepper-navigation>
            </q-step>
        </q-stepper>
    </form>
</template>
<script>
import {mapActions, mapGetters} from 'vuex'
import User from "../models/User"

export default {
    name: 'Form',
    model: {
        prop: 'UserObj',
        event: 'change'
    },
    props: {
        UserObj: {
            type: Object,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters(['permissions', 'permissions_loading', 'roles', 'countries']),
        step1_error() {
            let step1_field_list = [
                'gender' ,'first_name', 'last_name', 'mobile', 'email', 'password'
            ];
            let intercept = Object.keys(this.UserObj.errors.errors).filter(value => -1 !== step1_field_list.indexOf(value));
            return intercept.length > 0;
        },
        callingCode() {
            return this.UserObj.calling_code
        },
        countryId() {
            return this.UserObj.country_id
        },
        gender() {
            return this.UserObj.gender
        }
    },
    data() {
        return {
            step: 1,
            loading_countries: false,
            displayCallingCodes: false,
            displayGenders: false,
            filtered_countries: [],
            addresses: [],
            userCountry: {
                name: 'New Zealand', acronym: 'NZ', calling_code: '64'
            },
            userCountryByCallingCode: {
                name: 'New Zealand', acronym: 'NZ', calling_code: '64'
            },
            account_types: ['personal', 'business'],
            genders: [
                {label: 'Male', value: 'M', icon: 'fa fa-male', color: 'blue'},
                {label: 'Female', value: 'F', icon: 'fa fa-female', color: 'pink'},
                {label: 'Other', value: 'O', icon: 'fa fa-genderless', color: 'purple'},
                {label: 'N/A', value: null, icon: 'fa fa-question', color: 'gray'},
            ],
            userGender: {label: 'N/A', value: null, icon: 'fa fa-question'},
        }
    },
    methods: {
        ...mapActions(['set_countries']),
        provide_permissions(value) {
            this.$emit('load_permissions', value);
        },
        setCurrentCountry(country) {
            this.userCountryByCallingCode = country
            this.UserObj.calling_code = country.calling_code
        },
        setUserGender(gender) {
            this.userGender = gender
            this.UserObj.gender = gender.value
        },
        submit() {
            this.$emit('submit')
        },
        findCountryByCallingCode(calling_code) {
            const country = this.countries.find(country => country.calling_code === calling_code)
            return country ? country : {name: 'New Zealand', acronym: 'NZ', calling_code: '64'}
        },
        findCountryByCountryId() {
            const country = this.countries.find(country => country.id === this.UserObj.country_id)
            return country ? country : {name: 'New Zealand', acronym: 'NZ', calling_code: '64'}
        },
        filterCountries(val, update, abort) {
            if (val === '') {
                update(() => {
                    this.filtered_countries = this.countries
                })
                return
            }

            update(() => {
                const needle = val.toLowerCase()
                this.filtered_countries = this.countries.filter(country => country.name.toLowerCase().indexOf(needle) > -1)
            })
        },
        filterAddresses(val, update, abort) {
            if (val.length < 5 || !this.userCountry || !['NZ', 'AU'].includes(this.userCountry.acronym)) {
                abort()
                return
            }

            User.addresses(`q=${val}&c=${this.userCountry.acronym}`).then(response => {
                this.addressOptions = response
                update(() => {
                    this.addresses = this.addressOptions
                })
            }).catch(data => {
                abort()
            })
        },
        setAddress(address) {
            this.UserObj.address = address
        },
    },
    watch: {
        permissions(value) {
            this.UserObj.permissions = value.filter(v => v.checked === true).map(v => v.name);
        },
        callingCode(value) {
            if(value) {
                this.userCountryByCallingCode = this.findCountryByCallingCode(value)
            }
        },
        countryId(value) {
            if(value) {
                this.userCountry = this.findCountryByCountryId(value)
                this.userCountryByCallingCode = this.userCountry
                this.UserObj.calling_code = this.userCountry.calling_code
            }
        },
        gender(value) {
            if(value) {
                this.userGender = this.genders.find(gender => gender.value === value)
            }
        }
    },
    mounted() {
        if (! this.countries.length) {
            this.loading_countries = true
            this.set_countries().then(() => {
                this.loading_countries = false;
                this.userCountryByCallingCode = this.findCountryByCallingCode(this.UserObj.calling_code)
                this.userCountry = this.findCountryByCountryId(this.UserObj.country_id)
            }).catch(() => {this.loading_countries = false})
        }
        this.filtered_countries = this.countries
    },
    components: {},
}
</script>
